class SweepstakesCountdown extends HTMLElement {
  connectedCallback() {
    this.startDate = new Date(this.dataset.startDate || '');
    this.endDate = new Date(this.dataset.endDate || '');
    this.startTime = this.startDate.getTime();
    this.endTime = this.endDate.getTime();
    this.scope = this.closest('[data-countdown-scope]') || this.parentElement || this;
    this.output = this.querySelector('[data-countdown-status]');
    this.completeOutput = this.scope.querySelector('[data-countdown-complete-status]');
    this.label = this.scope.querySelector('[data-countdown-label]');
    this.progress = this.scope.querySelector('[data-countdown-progress]');
    this.progressFill = this.scope.querySelector('[data-countdown-progress-fill]');
    this.units = {
      days: this.querySelector('[data-countdown-days]'),
      hours: this.querySelector('[data-countdown-hours]'),
      minutes: this.querySelector('[data-countdown-minutes]'),
      seconds: this.querySelector('[data-countdown-seconds]'),
    };

    const requiresValidStart = this.hasAttribute('data-require-valid-start');
    const hasInvalidStart = Number.isNaN(this.startTime);
    const hasInvalidRange = !hasInvalidStart && this.endTime <= this.startTime;

    if (Number.isNaN(this.endTime) || (requiresValidStart && (hasInvalidStart || hasInvalidRange))) {
      if (!this.hasAttribute('data-editor-placeholder')) {
        this.scope.hidden = true;
      }
      return;
    }

    this.isVisible = !('IntersectionObserver' in window);
    this.handleVisibilityChange = () => {
      if (!document.hidden) this.update();
      this.syncTimer();
    };
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.isVisible = entry.isIntersecting;
        if (this.isVisible) this.update();
        this.syncTimer();
      });
      this.visibilityObserver.observe(this);
    }

    this.update();
    this.syncTimer();
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    this.visibilityObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  syncTimer() {
    window.clearInterval(this.timer);
    this.timer = undefined;

    const shouldRun = this.endTime > Date.now() && this.isVisible && !document.hidden;
    this.toggleAttribute('data-countdown-running', shouldRun);

    if (shouldRun) {
      this.timer = window.setInterval(() => this.update(), 1000);
    }
  }

  update() {
    const now = Date.now();
    const remaining = Math.max(0, this.endTime - now);
    const shouldAnnounceCompletion = this.previousRemaining > 0 && remaining === 0;
    this.previousRemaining = remaining;
    const totalSeconds = Math.floor(remaining / 1000);
    const values = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };

    for (const [unit, value] of Object.entries(values)) {
      const nextValue = String(value).padStart(2, '0');
      if (this.units[unit] && this.units[unit].textContent !== nextValue) {
        this.units[unit].textContent = nextValue;
      }
    }

    const status = `${values.days} days, ${values.hours} hours, ${values.minutes} minutes, and ${values.seconds} seconds remaining`;
    if (this.getAttribute('aria-label') !== status) this.setAttribute('aria-label', status);
    if (this.output && this.output.textContent !== status) this.output.textContent = status;

    if (this.progress && !Number.isNaN(this.startTime)) {
      const duration = this.endTime - this.startTime;
      const elapsed = now - this.startTime;
      const progressValue = duration > 0 ? Math.min(100, Math.max(0, (elapsed / duration) * 100)) : 0;
      this.progress.value = progressValue;
      const progressText = `${Math.round(progressValue)}% of the giveaway period elapsed`;
      if (this.progress.getAttribute('aria-valuetext') !== progressText) {
        this.progress.setAttribute('aria-valuetext', progressText);
      }
      if (this.progressFill?.hasAttribute('data-countdown-transform-progress')) {
        this.progressFill.style.setProperty('--boost-countdown-progress-ratio', progressValue / 100);
      } else if (this.progressFill) {
        this.progressFill.style.width = `${progressValue}%`;
      }
    }

    if (remaining === 0) {
      window.clearInterval(this.timer);
      this.timer = undefined;
      this.removeAttribute('data-countdown-running');
      this.dataset.complete = 'true';
      const completeMessage = this.dataset.completeMessage || 'Giveaway ended';
      this.setAttribute('aria-label', completeMessage);
      if (this.output) this.output.textContent = completeMessage;
      if (this.completeOutput && shouldAnnounceCompletion) this.completeOutput.textContent = completeMessage;
      if (this.label) this.label.textContent = completeMessage;
    }
  }
}

if (!customElements.get('sweepstakes-countdown')) {
  customElements.define('sweepstakes-countdown', SweepstakesCountdown);
}
